import { polymorphicFactory, Box, DsmColor, Spacer, Grid, GridColumn, AspectRatioKeeper, Image, PhotoOverlay, Flex, Typography, omitText, Link } from '@kohler-dsm-library/kohler-core';
import clsx from 'clsx';
import React, { isValidElement } from 'react';
import classes from './LargeImageAndTextBlock.module.css.mjs';
import { largeImageAndTextBlockImageContainerClasses, largeImageAndTextBlockTextPositionClasses, largeImageAndTextBlockImageAspectRatioClasses } from './LargeImageAndTextBlock.utils.mjs';

const LargeImageAndTextBlock = polymorphicFactory(
  (props, ref) => {
    const {
      className,
      style,
      component = "div",
      mod = "white",
      textPlacement = "center",
      showHeader = true,
      variant = "text-over-image",
      heading,
      copy,
      CTAs,
      gradientDirection = "tint",
      opacity = 35,
      topSpacer,
      bottomSpacer,
      image,
      ...rest
    } = props;
    const isTextOverImageVariant = variant === "text-over-image" || variant === "full-bleed-text-over-image";
    const isFullBleedTextOverImage = variant === "full-bleed-text-over-image";
    return /* @__PURE__ */ React.createElement(
      Box,
      {
        ref,
        mod,
        component,
        ...rest,
        style: { overflow: "hidden", maxWidth: "100%", ...style },
        backgroundColor: DsmColor.BackgroundPrimary,
        className: clsx("kohler-large-image-and-text-block-root", className)
      },
      topSpacer && /* @__PURE__ */ React.createElement(Spacer, { ...topSpacer }),
      /* @__PURE__ */ React.createElement(Grid, { className: clsx(!isFullBleedTextOverImage && classes.largeImageAndTextBlockContainerPadding) }, isTextOverImageVariant ? /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
        GridColumn,
        {
          className: clsx(
            largeImageAndTextBlockImageContainerClasses[variant],
            largeImageAndTextBlockTextPositionClasses[textPlacement]
          )
        },
        /* @__PURE__ */ React.createElement(AspectRatioKeeper, { className: largeImageAndTextBlockImageAspectRatioClasses[variant] }, /* @__PURE__ */ React.createElement(Image, { width: "100%", height: "100%", left: "0", top: "0", ...image, position: "absolute" }), /* @__PURE__ */ React.createElement(PhotoOverlay, { direction: gradientDirection, opacity }, showHeader && /* @__PURE__ */ React.createElement(
          Flex,
          {
            direction: "column",
            position: "absolute",
            className: clsx(
              isFullBleedTextOverImage ? classes.largeImageAndTextBlockTextContentTextOverImageFullBleed : classes.largeImageAndTextBlockTextContentTextOverImage,
              largeImageAndTextBlockTextPositionClasses[textPlacement]
            ),
            align: textPlacement === "center" ? "center" : "flex-start",
            textAlign: textPlacement === "left" ? "left" : "center"
          },
          heading && /* @__PURE__ */ React.createElement(
            Typography,
            {
              component: isValidElement(heading.text) ? "div" : heading.component || "h2",
              ...omitText(heading),
              size: "h2",
              textColor: DsmColor.ContentOnPhoto
            },
            heading.text
          ),
          copy && /* @__PURE__ */ React.createElement(
            Typography,
            {
              component: isValidElement(copy.text) ? "div" : copy.component || "p",
              className: classes.largeImageAndTextBlockCopyWithinHeading,
              ...omitText(copy),
              size: "paragraph-large",
              textColor: DsmColor.ContentOnPhoto
            },
            copy.text
          ),
          CTAs && /* @__PURE__ */ React.createElement(
            Flex,
            {
              className: classes.largeImageAndTextBlockCTAs,
              gap: "10px 30px",
              align: textPlacement === "left" ? "flex-start" : "center"
            },
            CTAs.map((CTA) => /* @__PURE__ */ React.createElement(Link, { key: CTA.text, size: "large", ...omitText(CTA), textColor: DsmColor.ContentOnPhoto }, CTA.text))
          )
        )))
      ), copy && /* @__PURE__ */ React.createElement(
        GridColumn,
        {
          className: clsx(classes.largeImageAndTextBlockCopyBelowHeading),
          paddingBottom: isFullBleedTextOverImage ? "50px" : void 0
        },
        /* @__PURE__ */ React.createElement(
          Typography,
          {
            component: isValidElement(copy.text) ? "div" : copy.component || "p",
            ...omitText(copy),
            size: "paragraph-large",
            textAlign: textPlacement === "left" ? "left" : "center",
            textColor: DsmColor.ContentSecondary
          },
          copy.text
        )
      )) : /* @__PURE__ */ React.createElement(React.Fragment, null, showHeader && /* @__PURE__ */ React.createElement(
        GridColumn,
        {
          display: "flex",
          className: clsx(
            classes.largeImageAndTextBlockTextContentTextAboveImage,
            largeImageAndTextBlockTextPositionClasses[textPlacement]
          ),
          textAlign: textPlacement === "left" ? "left" : "center",
          style: { alignItems: textPlacement === "left" ? "flex-start" : "center", flexDirection: "column" }
        },
        heading && /* @__PURE__ */ React.createElement(
          Typography,
          {
            component: isValidElement(heading.text) ? "div" : heading.component || "h2",
            ...omitText(heading),
            size: "h2",
            textColor: DsmColor.ContentPrimary
          },
          heading.text
        ),
        copy && /* @__PURE__ */ React.createElement(
          Typography,
          {
            component: isValidElement(copy.text) ? "div" : copy.component || "p",
            ...omitText(copy),
            textColor: DsmColor.ContentSecondary,
            size: "paragraph-large"
          },
          copy.text
        ),
        CTAs && /* @__PURE__ */ React.createElement(
          Flex,
          {
            className: classes.largeImageAndTextBlockCTAs,
            align: "flex-start",
            gap: "10px 30px",
            textAlign: textPlacement === "left" ? "left" : "center"
          },
          CTAs.map((CTA) => /* @__PURE__ */ React.createElement(Link, { key: CTA.text, ...omitText(CTA), size: "large" }, CTA.text))
        )
      ), /* @__PURE__ */ React.createElement(GridColumn, { className: largeImageAndTextBlockImageContainerClasses[variant] }, /* @__PURE__ */ React.createElement(AspectRatioKeeper, { className: largeImageAndTextBlockImageAspectRatioClasses[variant] }, /* @__PURE__ */ React.createElement(PhotoOverlay, { direction: gradientDirection, opacity }, /* @__PURE__ */ React.createElement(Image, { width: "100%", height: "100%", ...image })))))),
      bottomSpacer && /* @__PURE__ */ React.createElement(Spacer, { ...bottomSpacer })
    );
  }
);

export { LargeImageAndTextBlock };
